<template>
  <div v-if="enabled && !unlocked" class="gate">
    <div class="gate-card">
      <h2 class="gate-title">Protected Preview</h2>
      <p class="gate-text">Enter the access password to continue.</p>
      <form @submit.prevent="handleSubmit">
        <input
          v-model="passwordInput"
          class="gate-input"
          type="password"
          placeholder="Password"
          autocomplete="off"
        />
        <button class="gate-button" type="submit">Unlock</button>
        <p v-if="error" class="gate-error">{{ error }}</p>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';

const REQUIRED_PASSWORD = import.meta.env.VITE_PREVIEW_PASSWORD as string | undefined;
const STORAGE_KEY = 'app-preview-access';

const enabled = Boolean(REQUIRED_PASSWORD && REQUIRED_PASSWORD.trim().length > 0);
const unlocked = ref(false);
const passwordInput = ref('');
const error = ref<string | null>(null);

const handleSubmit = (): void => {
  if (!REQUIRED_PASSWORD) {
    unlocked.value = true;
    return;
  }
  if (passwordInput.value === REQUIRED_PASSWORD) {
    localStorage.setItem(STORAGE_KEY, 'granted');
    unlocked.value = true;
    error.value = null;
  } else {
    error.value = 'Incorrect password. Please try again.';
    unlocked.value = false;
  }
};

onMounted(() => {
  if (!enabled) {
    unlocked.value = true;
    return;
  }
  const flag = localStorage.getItem(STORAGE_KEY);
  if (flag === 'granted') {
    unlocked.value = true;
  }
});
</script>

<style scoped>
.gate {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.gate-card {
  background: #0f172a;
  color: #e2e8f0;
  padding: 24px;
  border-radius: 12px;
  width: 320px;
  box-shadow: 0 12px 50px rgba(0, 0, 0, 0.35);
}

.gate-title {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 700;
}

.gate-text {
  margin: 0 0 16px 0;
  font-size: 14px;
  color: #cbd5e1;
}

.gate-input {
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #334155;
  background: #0b1224;
  color: #e2e8f0;
  margin-bottom: 12px;
  outline: none;
}

.gate-input:focus {
  border-color: #38bdf8;
  box-shadow: 0 0 0 2px rgba(56, 189, 248, 0.25);
}

.gate-button {
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: none;
  background: #38bdf8;
  color: #0b1224;
  font-weight: 700;
  cursor: pointer;
}

.gate-button:hover {
  background: #22d3ee;
}

.gate-error {
  margin: 10px 0 0 0;
  color: #f97316;
  font-size: 13px;
}
</style>
